/**
 * Компактная панель групп слоёв тактической доски.
 */
import { useState } from "react";
import { Eye, EyeOff, FolderLock, Lock, LockOpen, Plus } from "lucide-react";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import type { TacticalLayerGroup } from "@/services/tactical.service";

type Props = {
  groups: TacticalLayerGroup[];
  canManage: boolean;
  selectedFeatureId: number | null;
  selectedFeatureGroupId: number | null | undefined;
  activeGroupIdForCreate: number | null;
  onActiveGroupForCreate: (id: number | null) => void;
  onCreate: (name: string) => void;
  onRename: (groupId: number, name: string) => void;
  onToggleVisible: (groupId: number, visible: boolean) => void;
  onToggleLock: (groupId: number, locked: boolean) => void;
  onDelete: (groupId: number) => void;
  onAssignSelected: (groupId: number | null) => void;
  busy?: boolean;
};

export function TacticalLayersPanel({
  groups,
  canManage,
  selectedFeatureId,
  selectedFeatureGroupId,
  activeGroupIdForCreate,
  onActiveGroupForCreate,
  onCreate,
  onRename,
  onToggleVisible,
  onToggleLock,
  onDelete,
  onAssignSelected,
  busy = false,
}: Props) {
  const [newName, setNewName] = useState("");
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const deleteTarget = groups.find((g) => g.id === deleteId) ?? null;

  return (
    <div className="space-y-2 border-t pt-2">
      <div className="flex items-center gap-1 px-1">
        <p className="text-xs font-medium text-muted-foreground">Группы слоёв</p>
        <SectionHelpTooltip label="Справка: группы слоёв">
          <p>Папки одного уровня для организации фич доски (не MVT ОИ).</p>
          <ul className="list-disc space-y-1 pl-4">
            <li>Глаз — скрыть группу на карте; замок — запрет правки фич группы.</li>
            <li>«Новые в…» — новые фичи попадают в выбранную группу.</li>
            <li>Выберите фичу на карте и назначьте группу селектом ниже.</li>
            <li>Удаление группы не удаляет фичи — они остаются без группы.</li>
          </ul>
        </SectionHelpTooltip>
      </div>

      {canManage && (
        <form
          className="flex gap-1 px-1"
          onSubmit={(e) => {
            e.preventDefault();
            const name = newName.trim();
            if (!name || busy) return;
            onCreate(name);
            setNewName("");
          }}
        >
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Новая группа"
            aria-label="Имя новой группы"
            className="h-7 text-xs"
          />
          <Button type="submit" size="icon" className="h-7 w-7 shrink-0" disabled={!newName.trim() || busy}>
            <Plus className="h-3.5 w-3.5" />
          </Button>
        </form>
      )}

      <div className="px-1">
        <label className="mb-0.5 block text-[10px] text-muted-foreground">Новые фичи в группу</label>
        <select
          className="h-7 w-full rounded-md border bg-background px-1.5 text-xs"
          value={activeGroupIdForCreate ?? ""}
          disabled={!canManage}
          aria-label="Группа для новых фич"
          onChange={(e) => {
            const v = e.target.value;
            onActiveGroupForCreate(v === "" ? null : Number(v));
          }}
        >
          <option value="">Без группы</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </select>
      </div>

      {selectedFeatureId != null && canManage && (
        <div className="px-1">
          <label className="mb-0.5 block text-[10px] text-muted-foreground">
            Группа выбранной фичи
          </label>
          <select
            className="h-7 w-full rounded-md border bg-background px-1.5 text-xs"
            value={selectedFeatureGroupId ?? ""}
            aria-label="Назначить группу выбранной фиче"
            disabled={busy}
            onChange={(e) => {
              const v = e.target.value;
              onAssignSelected(v === "" ? null : Number(v));
            }}
          >
            <option value="">Без группы</option>
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="max-h-48 space-y-1 overflow-y-auto px-1">
        {groups.length === 0 && (
          <p className="text-[11px] text-muted-foreground">Пока нет групп</p>
        )}
        {groups.map((group) => (
          <div
            key={group.id}
            className={cn(
              "rounded border border-border/60 p-1",
              group.is_visible === false && "opacity-60",
              activeGroupIdForCreate === group.id && "ring-1 ring-primary/40",
            )}
          >
            <div className="flex items-center gap-0.5">
              {group.is_locked ? (
                <FolderLock className="h-3 w-3 shrink-0 text-amber-700" aria-hidden />
              ) : null}
              {canManage ? (
                <Input
                  defaultValue={group.name}
                  className="h-6 text-[11px] font-medium"
                  aria-label={`Имя группы ${group.name}`}
                  onBlur={(e) => {
                    const next = e.target.value.trim();
                    if (next && next !== group.name) onRename(group.id, next);
                    else e.target.value = group.name;
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                  }}
                />
              ) : (
                <span className="min-w-0 flex-1 truncate px-1 text-[11px] font-medium">
                  {group.name}
                </span>
              )}
              {canManage && (
                <>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6"
                    title={group.is_visible !== false ? "Скрыть" : "Показать"}
                    aria-label={group.is_visible !== false ? "Скрыть группу" : "Показать группу"}
                    onClick={() => onToggleVisible(group.id, !(group.is_visible !== false))}
                  >
                    {group.is_visible !== false ? (
                      <Eye className="h-3 w-3" />
                    ) : (
                      <EyeOff className="h-3 w-3" />
                    )}
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6"
                    title={group.is_locked ? "Разблокировать" : "Заблокировать"}
                    aria-label={group.is_locked ? "Разблокировать группу" : "Заблокировать группу"}
                    onClick={() => onToggleLock(group.id, !group.is_locked)}
                  >
                    {group.is_locked ? (
                      <Lock className="h-3 w-3" />
                    ) : (
                      <LockOpen className="h-3 w-3" />
                    )}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-6 px-1.5 text-[11px]"
                    title="Удалить группу"
                    aria-label={`Удалить группу ${group.name}`}
                    onClick={() => setDeleteId(group.id)}
                  >
                    ×
                  </Button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <AlertDialog open={deleteId != null} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="z-[230]">
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить группу?</AlertDialogTitle>
            <AlertDialogDescription>
              Группа «{deleteTarget?.name ?? ""}» будет удалена. Фичи останутся на доске без
              группы — это нельзя отменить из UI.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={busy}
              onClick={() => {
                if (deleteId != null) onDelete(deleteId);
                setDeleteId(null);
              }}
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
